import type { AccessTokenClaims } from '@utils/access-token';

/**
 * Staff powers in next-jarvis: super admin (the full Master menu, cross-PPJK
 * lists, price columns, PIB calibration) and impersonation.
 *
 * They come from GET /api/v1/cms/me (backend#1678), the grants managed on the
 * CMS Access page. They used to come from a hard-coded email list here while
 * the backend allowed impersonation from IMPERSONATION_ALLOWED_EMAILS, so the
 * two disagreed: some staff saw "Switch user" and were then refused.
 *
 * This is still a UI gate; the backend enforces the same grants.
 */

export const IMPERSONATE_CAPABILITY = 'impersonate';

/** The `data` of GET /cms/me. A customer gets superAdmin false and empty lists, not a 403. */
export interface CmsMe {
  userId: number;
  email: string;
  superAdmin: boolean;
  projects: { key: string; role: string }[];
  capabilities: string[];
}

export const JARVIS_PROJECT_KEY = 'jarvis';
export const PROJECT_ADMIN_ROLE = 'admin';

export interface StaffAccess {
  superAdmin: boolean;
  canImpersonate: boolean;
  /**
   * May change JARVIS-wide master data that every tenant sees, such as the
   * "Tandai HS sebagai Spek Khusus" flag on an HS code. The write side of the
   * backend's cmsaccess.IsJarvisStaff: a super admin or a JARVIS admin (a
   * JARVIS viewer only reads). False during an impersonation, as the backend
   * evaluates the impersonated customer.
   */
  jarvisStaff: boolean;
}

export const NO_STAFF_ACCESS: StaffAccess = { superAdmin: false, canImpersonate: false, jarvisStaff: false };

/**
 * What the UI may offer, from a /cms/me answer. Anything missing or malformed
 * reads as no access.
 *
 * /cms/me describes the staff member behind an impersonation, so
 * canImpersonate holds through one (the banner's "Switch user"). superAdmin
 * gates screens that act as the user on screen, and those must look the way
 * the impersonated customer sees them: it is false during an impersonation.
 */
export function staffAccessFrom(me: CmsMe | null | undefined, impersonating: boolean): StaffAccess {
  if (!me) return NO_STAFF_ACCESS;
  const superAdmin = me.superAdmin === true;
  const capabilities = Array.isArray(me.capabilities) ? me.capabilities : [];
  const projects = Array.isArray(me.projects) ? me.projects : [];
  const jarvisAdmin = projects.some((grant) => grant?.key === JARVIS_PROJECT_KEY && grant?.role === PROJECT_ADMIN_ROLE);
  return {
    superAdmin: superAdmin && !impersonating,
    canImpersonate: superAdmin || capabilities.includes(IMPERSONATE_CAPABILITY),
    jarvisStaff: !impersonating && (superAdmin || jarvisAdmin),
  };
}

/**
 * The staff member a token acts for: the impersonator during an impersonation,
 * else the user. Null without a token. /cms/me answers the same for both
 * tokens of an impersonation, so this keys its cache.
 */
export function staffIdFrom(claims: AccessTokenClaims | null): string | null {
  if (!claims) return null;
  const id = claims.impersonated === true ? claims.impersonator_id ?? claims.user_id : claims.user_id;
  return id === undefined ? null : String(id);
}
